import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { Plus, Edit, Trash2, Eye, EyeOff, Save, X, Package, Image as ImageIcon } from 'lucide-react';

const ProductsManager = () => {
  const api = useApi();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Form state for creating/editing products
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo_url: '',
    features: [''],
    category: 'Enterprise',
    order: 0,
    is_active: true
  });

  const categoryOptions = [
    'Enterprise', 'Government', 'Education', 'Healthcare', 'Non-Profit', 
    'E-Commerce', 'SaaS', 'Custom'
  ];

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/products');
      if (response.success) {
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async () => {
    try {
      const productData = {
        ...formData,
        features: formData.features.filter(f => f.trim() !== ''),
        order: products.length + 1
      };

      const response = await api.post('/admin/products', productData);
      if (response.success) {
        loadProducts();
        resetForm();
        setShowCreateForm(false);
      }
    } catch (error) {
      console.error('Error creating product:', error);
    }
  };

  const handleUpdateProduct = async () => {
    try {
      const productData = {
        ...formData,
        features: formData.features.filter(f => f.trim() !== '')
      };

      const response = await api.put(`/admin/products/${editingProduct}`, productData);
      if (response.success) {
        loadProducts();
        resetForm();
        setEditingProduct(null);
      }
    } catch (error) {
      console.error('Error updating product:', error);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        const response = await api.delete(`/admin/products/${productId}`);
        if (response.success) {
          loadProducts();
        }
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const handleToggleActive = async (productId) => {
    try {
      const response = await api.put(`/admin/products/${productId}/toggle`);
      if (response.success) {
        loadProducts();
      }
    } catch (error) {
      console.error('Error toggling product status:', error);
    }
  };

  const startEdit = (product) => {
    setFormData({
      name: product.name,
      description: product.description,
      logo_url: product.logo_url,
      features: product.features.length > 0 ? product.features : [''],
      category: product.category,
      order: product.order,
      is_active: product.is_active
    });
    setEditingProduct(product.id);
    setShowCreateForm(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      logo_url: '',
      features: [''],
      category: 'Enterprise',
      order: 0,
      is_active: true
    });
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const updateFeature = (index, value) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map((feature, i) => i === index ? value : feature)
    }));
  };

  const removeFeature = (index) => {
    if (formData.features.length > 1) {
      setFormData(prev => ({
        ...prev,
        features: prev.features.filter((_, i) => i !== index)
      }));
    }
  };

  const ProductCard = ({ product }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          {product.logo_url ? (
            <img 
              src={product.logo_url} 
              alt={product.name}
              className="w-12 h-12 object-contain rounded-lg border border-gray-200"
            />
          ) : (
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
          )}
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{product.name}</h3>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <span className="bg-gray-100 px-2 py-1 rounded">{product.category}</span>
              <span>Order: {product.order}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleToggleActive(product.id)}
            className={`p-2 rounded-lg ${product.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
            title={product.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
          >
            {product.is_active ? <Eye size={18} /> : <EyeOff size={18} />}
          </button>
          <button
            onClick={() => startEdit(product)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
            title="Edit product"
          >
            <Edit size={18} />
          </button>
          <button
            onClick={() => handleDeleteProduct(product.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
            title="Delete product"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
      
      <p className="text-gray-600 mb-4 text-sm">{product.description}</p>
      
      {product.features && product.features.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Features</p>
          <div className="flex flex-wrap gap-1">
            {product.features.map((feature, index) => (
              <span
                key={index}
                className="inline-block bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded"
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const ProductForm = () => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          {editingProduct ? 'Edit Product' : 'Create New Product'}
        </h3>
        <button
          onClick={() => {
            setEditingProduct(null);
            setShowCreateForm(false);
            resetForm();
          }}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg"
        >
          <X size={18} />
        </button>
      </div>

      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Product Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Product name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData(prev => ({...prev, category: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {categoryOptions.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Product description"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Logo URL</label>
          <div className="relative">
            <ImageIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="url"
              value={formData.logo_url}
              onChange={(e) => setFormData(prev => ({...prev, logo_url: e.target.value}))}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="https://example.com/logo.png"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">Features</label>
            <button
              type="button"
              onClick={addFeature}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              + Add Feature
            </button>
          </div>
          <div className="space-y-2">
            {formData.features.map((feature, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="text"
                  value={feature}
                  onChange={(e) => updateFeature(index, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Feature description"
                />
                {formData.features.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="is_active"
            checked={formData.is_active}
            onChange={(e) => setFormData(prev => ({...prev, is_active: e.target.checked}))}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
            Active (visible to public)
          </label>
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            onClick={editingProduct ? handleUpdateProduct : handleCreateProduct}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save size={18} />
            <span>{editingProduct ? 'Update Product' : 'Create Product'}</span>
          </button>
          <button
            onClick={() => {
              setEditingProduct(null);
              setShowCreateForm(false);
              resetForm();
            }}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Products Manager</h2>
          <p className="text-gray-600 mt-1">Manage your product portfolio and showcase</p>
        </div>
        <button
          onClick={() => {
            setShowCreateForm(true);
            setEditingProduct(null);
            resetForm();
          }}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={18} />
          <span>Add Product</span>
        </button>
      </div>

      {(showCreateForm || editingProduct) && <ProductForm />}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {products.map(product => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>

      {products.length === 0 && !showCreateForm && (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No products found</p>
          <button
            onClick={() => {
              setShowCreateForm(true);
              resetForm();
            }}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Create your first product
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductsManager;